import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";

const CATS = [
    { id: "all", label: "All pieces" },
    { id: "clothing", label: "Clothing" },
    { id: "home", label: "Home" },
    { id: "office", label: "Office" },
];

const SORTS = [
    { id: "new", label: "Newest" },
    { id: "low", label: "Price · low to high" },
    { id: "high", label: "Price · high to low" },
];

export default function Shop() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState(searchParams.get("q") || "");
    const [sort, setSort] = useState("new");

    const activeCat = searchParams.get("cat") || "all";

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (activeCat !== "all") params.category = activeCat;
        if (q) params.q = q;
        api.get("/products", { params })
            .then(({ data }) => setProducts(data.products))
            .finally(() => setLoading(false));
    }, [activeCat, q]);

    const sorted = useMemo(() => {
        const arr = [...products];
        if (sort === "low") arr.sort((a, b) => a.price - b.price);
        if (sort === "high") arr.sort((a, b) => b.price - a.price);
        return arr;
    }, [products, sort]);

    const setCat = (cat) => {
        const next = new URLSearchParams(searchParams);
        if (cat === "all") next.delete("cat");
        else next.set("cat", cat);
        setSearchParams(next);
    };

    return (
        <div className="bg-obsidian" data-testid="shop-page">
            <section className="border-b border-border-dim">
                <div className="mx-auto max-w-[1480px] px-6 py-16 lg:px-12">
                    <p className="text-[11px] uppercase tracking-[0.4em] text-gold">The collection</p>
                    <h1 className="mt-3 font-display text-4xl text-white sm:text-6xl">Atelier 04</h1>
                    <p className="mt-4 max-w-2xl text-white/60">
                        Browse the full LovinLife edit — clothing for slow mornings, objects for full evenings, tools for the work you love.
                    </p>
                </div>
            </section>

            <section className="border-b border-border-dim">
                <div className="mx-auto flex max-w-[1480px] flex-col items-stretch justify-between gap-4 px-6 py-6 lg:flex-row lg:items-center lg:px-12">
                    <div className="flex flex-wrap gap-2">
                        {CATS.map((c) => (
                            <button
                                key={c.id}
                                onClick={() => setCat(c.id)}
                                data-testid={`filter-${c.id}`}
                                className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] transition-colors ${
                                    activeCat === c.id
                                        ? "border border-gold bg-gold text-obsidian"
                                        : "border border-border-dim text-white/70 hover:border-gold hover:text-gold"
                                }`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-3">
                        <input
                            data-testid="shop-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search pieces…"
                            className="w-full border border-border-dim bg-transparent px-4 py-2.5 text-sm text-white outline-none placeholder:text-white/30 focus:border-gold lg:w-64"
                        />
                        <div className="relative">
                            <select
                                value={sort}
                                onChange={(e) => setSort(e.target.value)}
                                data-testid="shop-sort"
                                className="appearance-none border border-border-dim bg-transparent py-2.5 pl-4 pr-10 text-sm text-white outline-none focus:border-gold"
                            >
                                {SORTS.map((s) => (
                                    <option key={s.id} value={s.id} className="bg-obsidian">
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                            <SlidersHorizontal className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                        </div>
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-[1480px] px-6 py-12 lg:px-12">
                {loading ? (
                    <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="aspect-[4/5] animate-pulse bg-surface" />
                        ))}
                    </div>
                ) : sorted.length === 0 ? (
                    <div className="py-24 text-center">
                        <p className="font-display text-3xl text-white/70">Nothing matches — yet.</p>
                        <p className="mt-2 text-white/40">Try another category or search.</p>
                    </div>
                ) : (
                    <div
                        data-testid="product-grid"
                        className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-3 lg:grid-cols-4"
                    >
                        {sorted.map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
